import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { FaTelegramPlane } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import { formatTime, parseOrderItems, calculateOrderTotal, formatPrice, cn } from '../lib/utils';
import { COLUMNS } from '../data/mockOrders';
import { api } from '../services/api';

const { FiX, FiPhone, FiMapPin, FiUser, FiShoppingBag, FiCheck, FiChevronDown } = FiIcons;

const OrderModal = ({ order, onClose }) => {
  const [currentStatus, setCurrentStatus] = useState(order?.status);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Sync state when a new order is passed to the modal
  useEffect(() => {
    if (order?.status) {
      setCurrentStatus(order.status);
    }
  }, [order]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!order) return null;

  const items = parseOrderItems(order.order);
  const totalAmount = calculateOrderTotal(items);

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus || isUpdating) return;
    
    setIsUpdating(true);
    setIsDropdownOpen(false);
    const oldStatus = currentStatus;
    setCurrentStatus(newStatus);

    try {
      await api.updateOrderStatus(order._id || order.id, newStatus);
      // Trigger refresh of kanban board
      window.dispatchEvent(new Event('kanban-refresh-trigger'));
    } catch (error) {
      console.error('Failed to update status:', error);
      setCurrentStatus(oldStatus);
      alert('Failed to update status. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const currentColumn = COLUMNS[currentStatus];

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm"
        // Ensure it doesn't go under iOS status bar/notch
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-5xl bg-yellow-400 text-black rounded-3xl shadow-2xl flex flex-col max-h-full overflow-hidden border border-yellow-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Fixed Header */}
          <div className="shrink-0 flex items-center justify-between p-4 lg:p-6 border-b-2 border-black/10 bg-yellow-400 z-20">
            <div>
              <div className="flex items-center gap-2 lg:gap-3 mb-1">
                <h2 className="text-2xl lg:text-3xl font-black tracking-tight">
                  #{order.tilda_order_id}
                </h2>
              </div>
              <span className="text-xs lg:text-sm font-bold opacity-60 flex items-center gap-1.5">
                <SafeIcon icon={FiIcons.FiClock} size={14} />
                {formatTime(order.created_at)}
              </span>
            </div>
            <button 
              onClick={onClose}
              className="p-3 bg-black/5 hover:bg-black/10 rounded-full transition-colors"
            >
              <SafeIcon icon={FiX} size={24} className="text-black" />
            </button>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto scrollbar-hide p-4 lg:p-6 bg-yellow-400">
            
            {/* Compact Status Dropdown */}
            <div className="mb-6 lg:mb-8" ref={dropdownRef}>
              <h3 className="text-xs font-black uppercase opacity-50 mb-2 tracking-wider ml-1">
                Current Status {isUpdating && <span className="animate-pulse ml-2 text-black">Updating...</span>}
              </h3>
              <div className="relative">
                <button
                  onClick={() => !isUpdating && setIsDropdownOpen(!isDropdownOpen)}
                  disabled={isUpdating}
                  className={cn(
                    "w-full flex items-center justify-between px-5 py-4 bg-black text-yellow-400 rounded-2xl font-black text-sm lg:text-base uppercase tracking-wide transition-all shadow-lg",
                    isUpdating && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <span className="flex items-center gap-3">
                    {currentColumn?.title || currentStatus}
                  </span>
                  <SafeIcon icon={FiChevronDown} className={cn("transition-transform duration-300", isDropdownOpen && "rotate-180")} size={20} />
                </button>

                <AnimatePresence>
                  {isDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-0 right-0 mt-2 bg-black rounded-2xl shadow-2xl z-50 overflow-hidden border border-zinc-800"
                    >
                      {Object.entries(COLUMNS).map(([statusKey, statusData]) => {
                        const isSelected = statusKey === currentStatus;
                        return (
                          <button
                            key={statusKey}
                            onClick={() => handleStatusChange(statusKey)}
                            className={cn(
                              "w-full flex items-center justify-between px-5 py-4 transition-colors text-sm lg:text-base font-black uppercase tracking-wide border-b border-zinc-800/50 last:border-b-0",
                              isSelected ? "bg-zinc-900 text-yellow-400" : "text-zinc-300 hover:text-yellow-400 hover:bg-zinc-900"
                            )}
                          >
                            {statusData.title}
                            {isSelected && <SafeIcon icon={FiCheck} size={18} />}
                          </button>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              
              {/* Left Column - Details */}
              <div className="space-y-4 lg:space-y-6">
                
                <div className="grid grid-cols-2 gap-3 lg:gap-4">
                  <div className="bg-black text-yellow-400 p-4 lg:p-5 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-2 mb-2 opacity-70">
                      <SafeIcon icon={FiMapPin} size={16} />
                      <span className="text-xs font-black uppercase">Hall</span>
                    </div>
                    <div className="text-3xl lg:text-4xl font-black">{order.hall}</div>
                  </div>
                  
                  <div className="bg-black text-yellow-400 p-4 lg:p-5 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-2 mb-2 opacity-70">
                      <SafeIcon icon={FiMapPin} size={16} />
                      <span className="text-xs font-black uppercase">Row / Seat</span>
                    </div>
                    <div className="text-3xl lg:text-4xl font-black">
                      {order.hall_row} / {order.hall_seat}
                    </div>
                  </div>
                </div>

                {/* Customer Section */}
                <div className="bg-white/30 p-4 lg:p-5 rounded-2xl">
                  <h3 className="text-xs font-black uppercase opacity-60 mb-3 tracking-wider">
                    Customer
                  </h3>
                  
                  <div className="flex flex-col gap-4">
                    {/* User Details */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center text-yellow-400 flex-shrink-0">
                        <SafeIcon icon={FiUser} size={20} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-lg leading-tight break-words">{order.name}</div>
                        <div className="text-sm font-bold opacity-60 break-words mt-0.5">{order.phone}</div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 w-full">
                      <a 
                        href={`tel:${order.phone}`}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-black text-yellow-400 rounded-xl font-black text-sm hover:scale-105 transition-transform shadow-md"
                      >
                        <SafeIcon icon={FiPhone} size={16} />
                        Call
                      </a>
                      {order.phone && (
                        <a
                          href={`https://t.me/${order.phone.replace(/[^0-9+]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-black text-yellow-400 rounded-xl font-black text-sm hover:scale-105 transition-transform shadow-md"
                          title="Написать в Telegram"
                        >
                          <FaTelegramPlane size={18} />
                          Telegram
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {order.comments && (
                  <div className="bg-black/5 p-4 lg:p-5 rounded-2xl border border-black/10">
                    <h3 className="text-xs font-black uppercase opacity-60 mb-2 tracking-wider">
                      Note
                    </h3>
                    <div className="italic font-bold text-base lg:text-lg">
                      "{order.comments}"
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Receipt */}
              <div className="bg-white/30 rounded-2xl p-4 lg:p-6 flex flex-col">
                <div className="flex items-center gap-2 mb-4 lg:mb-6">
                  <SafeIcon icon={FiShoppingBag} size={20} className="text-black" />
                  <h3 className="text-base lg:text-lg font-black uppercase tracking-wider">Order Receipt</h3>
                </div>

                <div className="grid grid-cols-12 gap-2 pb-3 mb-4 border-b-2 border-black/20 text-xs font-black uppercase opacity-60">
                  <div className="col-span-6">Item</div>
                  <div className="col-span-2 text-center">Qty</div>
                  <div className="col-span-4 text-right">Total</div>
                </div>

                <div className="space-y-3 mb-6 flex-1">
                  {items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center text-sm lg:text-base font-bold">
                      <div className="col-span-6 leading-tight break-words pr-2">{item.name}</div>
                      <div className="col-span-2 text-center bg-black/10 rounded-lg py-1 font-black">
                        {item.quantity}
                      </div>
                      <div className="col-span-4 text-right font-black">
                        {formatPrice(item.totalPrice)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t-2 border-black/20 pt-4 mt-auto">
                  <div className="flex justify-between items-center">
                    <span className="text-sm lg:text-base font-black uppercase opacity-60">Total Amount</span>
                    <span className="text-3xl lg:text-4xl font-black">
                      {formatPrice(totalAmount)}
                    </span>
                  </div>
                  <div className="text-xs font-bold opacity-60 mt-1 text-right">
                    {items.reduce((sum, item) => sum + item.quantity, 0)} items total
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Fixed Footer Actions */}
          <div className="shrink-0 p-4 lg:p-6 border-t-2 border-black/10 bg-yellow-500/20 z-20">
            <button
              onClick={onClose}
              className="w-full py-4 bg-black text-yellow-400 font-black text-base lg:text-lg rounded-2xl hover:bg-zinc-900 transition-colors uppercase tracking-widest shadow-xl"
            >
              Close Ticket
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default OrderModal;